import { useEffect, useState } from 'react';
import savingsService from '@/services/savingsService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, PiggyBank, Target, Calendar } from 'lucide-react';

const SavingsGoals = () => {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [showFundDialog, setShowFundDialog] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [fundMode, setFundMode] = useState('add'); // 'add' or 'withdraw'
    const [fundAmount, setFundAmount] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        targetAmount: '',
        currentAmount: 0,
        deadline: '',
        color: '#3b82f6',
    });

    useEffect(() => {
        fetchGoals();
    }, []);

    const fetchGoals = async () => {
        try {
            const data = await savingsService.getAll();
            setGoals(data);
        } catch (error) {
            toast.error('Failed to load savings goals');
        } finally {
            setLoading(false);
        }
    };

    const calculateProgress = (current, target) => {
        if (target <= 0) return 0;
        const percentage = (current / target) * 100;
        return Math.min(percentage, 100);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const handleEdit = (goal) => {
        setEditingGoal(goal);
        setFormData({
            name: goal.name,
            targetAmount: goal.targetAmount,
            currentAmount: goal.currentAmount,
            deadline: goal.deadline || '',
            color: goal.color || '#3b82f6',
        });
        setShowDialog(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this savings goal?')) return;
        try {
            await savingsService.remove(id);
            toast.success('Goal deleted');
            fetchGoals();
        } catch (error) {
            toast.error('Failed to delete goal');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingGoal) {
                await savingsService.update(editingGoal.id, formData);
                toast.success('Goal updated');
            } else {
                await savingsService.create(formData);
                toast.success('Goal created');
            }
            setShowDialog(false);
            resetForm();
            fetchGoals();
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    const handleFundSubmit = async (e) => {
        e.preventDefault();
        if (!selectedGoal || !fundAmount) return;

        try {
            if (fundMode === 'add') {
                await savingsService.addFunds(selectedGoal.id, fundAmount);
                toast.success('Funds added to goal');
            } else {
                await savingsService.withdrawFunds(selectedGoal.id, fundAmount);
                toast.success('Funds withdrawn from goal');
            }
            setShowFundDialog(false);
            setFundAmount('');
            setSelectedGoal(null);
            fetchGoals();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const openFundDialog = (goal, mode) => {
        setSelectedGoal(goal);
        setFundMode(mode);
        setFundAmount('');
        setShowFundDialog(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            targetAmount: '',
            currentAmount: 0,
            deadline: '',
            color: '#3b82f6',
        });
        setEditingGoal(null);
    };

    if (loading) {
        return <div className="p-8 text-center">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Savings Goals</h1>
                    <p className="text-muted-foreground mt-2">Track your financial targets</p>
                </div>
                <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                    <Plus className="mr-2 h-4 w-4" />
                    New Goal
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {goals.map((goal) => {
                    const progress = calculateProgress(goal.currentAmount, goal.targetAmount);
                    return (
                        <Card key={goal.id} className="relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-1 h-full transition-all group-hover:w-2" style={{ backgroundColor: goal.color }}></div>
                            <CardHeader className="pb-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <CardTitle className="text-xl">{goal.name}</CardTitle>
                                        {goal.deadline && (
                                            <div className="flex items-center text-xs text-muted-foreground mt-1">
                                                <Calendar className="h-3 w-3 mr-1" />
                                                Target: {goal.deadline}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(goal)}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(goal.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pb-2">
                                <div className="mt-2 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Progress</span>
                                        <span className="font-medium">{progress.toFixed(0)}%</span>
                                    </div>
                                    <Progress value={progress} className="h-2" />
                                    <div className="flex justify-between items-end mt-4">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Saved</p>
                                            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(goal.currentAmount)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-muted-foreground">Target</p>
                                            <p className="font-semibold">{formatCurrency(goal.targetAmount)}</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter className="pt-4 border-t bg-muted/20 flex gap-2">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    size="sm"
                                    onClick={() => openFundDialog(goal, 'withdraw')}
                                    disabled={goal.currentAmount <= 0}
                                >
                                    Withdraw
                                </Button>
                                <Button
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    size="sm"
                                    onClick={() => openFundDialog(goal, 'add')}
                                >
                                    Add Funds
                                </Button>
                            </CardFooter>
                        </Card>
                    );
                })}

                {goals.length === 0 && (
                    <div className="col-span-full flex flex-col items-center justify-center p-12 border-2 border-dashed rounded-lg text-center">
                        <PiggyBank className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium">No savings goals yet</h3>
                        <p className="text-muted-foreground mb-4 max-w-sm">Create a goal to start tracking your savings progress visually.</p>
                        <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                            Create Goal
                        </Button>
                    </div>
                )}
            </div>

            {/* Create/Edit Goal Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingGoal ? 'Edit Goal' : 'New Savings Goal'}</DialogTitle>
                        <DialogDescription>Set a target and track your progress.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Goal Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., New Laptop, Vacation"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Target Amount</Label>
                            <Input
                                type="number"
                                value={formData.targetAmount}
                                onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
                                placeholder="0.00"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Target Date (Optional)</Label>
                            <Input
                                type="date"
                                value={formData.deadline}
                                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Color Code</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    placeholder="#000000"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                            <Button type="submit">{editingGoal ? 'Update' : 'Create'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Add/Withdraw Funds Dialog */}
            <Dialog open={showFundDialog} onOpenChange={setShowFundDialog}>
                <DialogContent className="sm:max-w-xs">
                    <DialogHeader>
                        <DialogTitle>{fundMode === 'add' ? 'Add Funds' : 'Withdraw Funds'}</DialogTitle>
                        <DialogDescription>
                            {selectedGoal?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleFundSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Amount</Label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                                <Input
                                    type="number"
                                    value={fundAmount}
                                    onChange={(e) => setFundAmount(e.target.value)}
                                    placeholder="0.00"
                                    required
                                    autoFocus
                                    className="pl-7"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowFundDialog(false)}>Cancel</Button>
                            <Button
                                type="submit"
                                variant={fundMode === 'withdraw' ? 'destructive' : 'default'}
                                className={fundMode === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                            >
                                {fundMode === 'add' ? 'Add Funds' : 'Withdraw'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SavingsGoals;
